import { useState, useMemo } from 'react';
import {
  Server,
  Database,
  HardDrive,
  Globe,
  FolderOpen,
  Network,
  ChevronDown,
  ChevronRight,
  Circle,
} from 'lucide-react';
import { useApi } from '@/hooks/use-api';
import { Header } from '@/components/layout/header';
import { LoadingState } from '@/components/loading-state';
import { ErrorState } from '@/components/error-state';
import { CpuCell } from '@/components/cpu-cell';
import { StatusBadge, type Status } from '@/components/status-badge';
import { ec2InstanceStatus } from '@/lib/status';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MetricTooltip } from '@/components/metric-tooltip';
import { formatBytes, formatPercent, formatRelativeTime } from '@/lib/format';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EC2Data {
  instances: {
    instanceId: string;
    state: string;
    type: string;
    launchTime: string;
    publicIp: string;
    privateIp: string;
    tags: Record<string, string>;
    cpu: number | null;
  }[];
  elasticIp: {
    allocationId: string;
    publicIp: string;
    associatedInstanceId: string;
    isAssociated: boolean;
  };
}

interface RDSData {
  instances: {
    id: string;
    status: string;
    engine: string;
    instanceClass: string;
    allocatedStorageGb: number;
    endpoint: string;
    multiAz: boolean;
    cpu: number | null;
    connections: number | null;
    freeableMemoryBytes: number | null;
    freeStorageBytes: number | null;
  }[];
}

interface S3Data {
  buckets: {
    name: string;
    sizeBytes: number | null;
    objectCount: number | null;
  }[];
}

interface CloudFrontData {
  distributionId: string;
  domainName: string;
  status: string;
  enabled: boolean;
  aliases: string[];
  lastModified: string;
  requestsLast24h: number | null;
  errorRate4xx: number | null;
  errorRate5xx: number | null;
}

interface EFSData {
  fileSystems: {
    id: string;
    name: string;
    sizeBytes: number;
    state: string;
    numberOfMountTargets: number;
  }[];
}

interface Route53Data {
  hostedZoneId: string;
  records: {
    name: string;
    type: string;
    ttl: number;
    values: string[];
    aliasTarget: { dnsName: string } | null;
  }[];
}

function rdsStatus(status: string): Status {
  if (status === 'available') return 'healthy';
  if (status === 'starting' || status === 'backing-up' || status === 'modifying') return 'warning';
  if (status === 'stopped' || status === 'failed') return 'critical';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Health Summary Bar
// ---------------------------------------------------------------------------

interface SystemHealth {
  name: string;
  icon: React.ReactNode;
  status: Status;
}

function deriveHealth(
  ec2: EC2Data | null,
  rds: RDSData | null,
  s3: S3Data | null,
  cloudfront: CloudFrontData | null,
  efs: EFSData | null
): SystemHealth[] {
  return [
    {
      name: 'EC2',
      icon: <Server className="h-3.5 w-3.5" />,
      status: ec2
        ? ec2.instances.some((i) => i.state === 'running') && ec2.elasticIp.isAssociated
          ? 'healthy'
          : 'critical'
        : 'unknown',
    },
    {
      name: 'RDS',
      icon: <Database className="h-3.5 w-3.5" />,
      status: rds?.instances[0] ? rdsStatus(rds.instances[0].status) : 'unknown',
    },
    {
      name: 'S3',
      icon: <FolderOpen className="h-3.5 w-3.5" />,
      status: s3
        ? s3.buckets.some((b) => (b.objectCount ?? 0) > 0 || b.sizeBytes !== null)
          ? 'healthy'
          : 'warning'
        : 'unknown',
    },
    {
      name: 'CloudFront',
      icon: <Globe className="h-3.5 w-3.5" />,
      status: cloudfront
        ? cloudfront.enabled && cloudfront.status === 'Deployed'
          ? 'healthy'
          : 'warning'
        : 'unknown',
    },
    {
      name: 'EFS',
      icon: <HardDrive className="h-3.5 w-3.5" />,
      status: efs
        ? efs.fileSystems.every((fs) => fs.state === 'available')
          ? 'healthy'
          : 'warning'
        : 'unknown',
    },
  ];
}

const statusDotColor: Record<Status, string> = {
  healthy: 'text-muted-foreground/40',
  warning: 'text-[var(--status-warning-fg)]',
  critical: 'text-[var(--status-danger-fg)]',
  unknown: 'text-muted-foreground/20',
};

function HealthSummaryBar({ systems }: { systems: SystemHealth[] }) {
  const allHealthy = systems.every((s) => s.status === 'healthy' || s.status === 'unknown');

  return (
    <div className="flex items-center gap-6 border-b px-6 py-3">
      {allHealthy ? (
        <span className="text-muted-foreground text-sm">All systems normal</span>
      ) : (
        <span className="text-sm font-medium">Systems</span>
      )}
      <div className="flex items-center gap-5">
        {systems.map((sys) => (
          <div key={sys.name} className="flex items-center gap-1.5">
            <Circle className={`h-2 w-2 fill-current ${statusDotColor[sys.status]}`} />
            <span
              className={`text-xs ${
                sys.status === 'warning' || sys.status === 'critical'
                  ? 'font-medium'
                  : 'text-muted-foreground'
              }`}
              style={
                sys.status === 'warning'
                  ? { color: 'var(--status-warning-fg)' }
                  : sys.status === 'critical'
                    ? { color: 'var(--status-danger-fg)' }
                    : undefined
              }
            >
              {sys.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: EC2
// ---------------------------------------------------------------------------

function EC2Section({ data }: { data: EC2Data }) {
  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <Server className="text-muted-foreground h-4 w-4" />
        <h2 className="text-sm font-medium">EC2 Instances</h2>
        <MetricTooltip metricKey="ec2.instance_count" />
      </div>

      <div className="mb-4 flex items-baseline gap-6 text-sm">
        <div>
          <span className="text-muted-foreground">Instances </span>
          <span className="font-medium">
            {data.instances.filter((i) => i.state === 'running').length}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Elastic IP </span>
          <span className="font-mono text-xs">{data.elasticIp.publicIp}</span>
          {!data.elasticIp.isAssociated && <StatusBadge status="critical" label="not associated" />}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Instance ID</TableHead>
            <TableHead>State</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>CPU</TableHead>
            <TableHead>Uptime</TableHead>
            <TableHead>Deploy SHA</TableHead>
            <TableHead>Public IP</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.instances.map((inst) => (
            <TableRow key={inst.instanceId}>
              <TableCell className="font-mono text-xs">{inst.instanceId}</TableCell>
              <TableCell>
                <StatusBadge status={ec2InstanceStatus(inst.state)} label={inst.state} />
              </TableCell>
              <TableCell>{inst.type}</TableCell>
              <TableCell>
                <CpuCell cpu={inst.cpu} />
              </TableCell>
              <TableCell>{formatRelativeTime(new Date(inst.launchTime))}</TableCell>
              <TableCell className="font-mono text-xs">
                {inst.tags['deploy-sha']?.slice(0, 7) ?? '—'}
              </TableCell>
              <TableCell className="font-mono text-xs">{inst.publicIp || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: RDS
// ---------------------------------------------------------------------------

function RDSSection({ data }: { data: RDSData }) {
  const db = data.instances[0];
  if (!db) return null;

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <Database className="text-muted-foreground h-4 w-4" />
        <h2 className="text-sm font-medium">RDS Database</h2>
        <StatusBadge status={rdsStatus(db.status)} label={db.status} />
      </div>

      <div className="mb-2 grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
        <div>
          <p className="text-muted-foreground text-xs">CPU</p>
          <p className="text-lg font-semibold">{db.cpu !== null ? formatPercent(db.cpu) : 'N/A'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Connections</p>
          <p className="text-lg font-semibold">
            {db.connections !== null ? Math.round(db.connections) : 'N/A'}
          </p>
          <p className="text-muted-foreground text-xs">Pool max: 20</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Free Memory</p>
          <p className="text-lg font-semibold">
            {db.freeableMemoryBytes !== null ? formatBytes(db.freeableMemoryBytes) : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">Free Storage</p>
          <p className="text-lg font-semibold">
            {db.freeStorageBytes !== null ? formatBytes(db.freeStorageBytes) : 'N/A'}
          </p>
          <p className="text-muted-foreground text-xs">{db.allocatedStorageGb} GB allocated</p>
        </div>
      </div>

      <p className="text-muted-foreground text-xs">
        {db.engine} &middot; {db.instanceClass}
        {db.multiAz ? ' · Multi-AZ' : ''}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section: S3 / CloudFront / EFS (secondary — less visual weight)
// ---------------------------------------------------------------------------

const BUCKET_INFO: Record<string, { label: string; description: string }> = {
  'overhead-frontend-prod': {
    label: 'Frontend Assets',
    description: 'Static SPA files served via CloudFront (JS, CSS, HTML)',
  },
  'overhead-rawdata-prod': {
    label: 'Raw POS Data',
    description: 'Immutable NDJSON payloads from POS integrations (source of truth)',
  },
};

function SecondaryServices({
  s3,
  cloudfront,
  efs,
}: {
  s3: S3Data | null;
  cloudfront: CloudFrontData | null;
  efs: EFSData | null;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* S3 */}
      {s3 && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <FolderOpen className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
              S3 Storage
            </h3>
            <MetricTooltip metricKey="s3.overview" />
          </div>
          <div className="space-y-3">
            {s3.buckets.map((b) => {
              const info = BUCKET_INFO[b.name];
              return (
                <div key={b.name}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{info?.label ?? b.name}</span>
                    <span>{b.sizeBytes !== null ? formatBytes(b.sizeBytes) : '—'}</span>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {b.objectCount !== null
                      ? `${b.objectCount.toLocaleString()} files`
                      : 'Object count unavailable'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* CloudFront */}
      {cloudfront && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Globe className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
              CloudFront
            </h3>
            <MetricTooltip metricKey="cloudfront.overview" />
            <StatusBadge
              status={
                cloudfront.enabled && cloudfront.status === 'Deployed' ? 'healthy' : 'warning'
              }
              label={cloudfront.status}
            />
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Requests (24h)</span>
              <span>{cloudfront.requestsLast24h?.toLocaleString() ?? 'N/A'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">4xx Error Rate</span>
              <span>
                {cloudfront.errorRate4xx !== null ? formatPercent(cloudfront.errorRate4xx) : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">5xx Error Rate</span>
              <span>
                {cloudfront.errorRate5xx !== null ? formatPercent(cloudfront.errorRate5xx) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* EFS */}
      {efs && (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <HardDrive className="text-muted-foreground h-3.5 w-3.5" />
            <h3 className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
              EFS
            </h3>
            <MetricTooltip metricKey="efs.overview" />
          </div>
          {efs.fileSystems.map((fs) => (
            <div key={fs.id}>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">{formatBytes(fs.sizeBytes)}</span>
                <StatusBadge
                  status={fs.state === 'available' ? 'healthy' : 'warning'}
                  label={fs.state}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                {fs.numberOfMountTargets} mount target
                {fs.numberOfMountTargets !== 1 ? 's' : ''} &middot; TLS certs and shard staging
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section: Route 53 (collapsible)
// ---------------------------------------------------------------------------

function Route53Section({ data }: { data: Route53Data }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <section>
      <button
        onClick={() => setExpanded(!expanded)}
        className="hover:text-foreground text-muted-foreground flex w-full items-center gap-2 py-2 text-left text-sm transition-colors"
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Network className="h-3.5 w-3.5" />
        <span className="font-medium">DNS Records</span>
        <MetricTooltip metricKey="route53.overview" />
        <span className="text-xs font-normal">{data.records.length} records</span>
      </button>

      {expanded && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>TTL</TableHead>
              <TableHead>Value</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.records.map((r, i) => (
              <TableRow key={`${r.name}-${r.type}-${i}`}>
                <TableCell className="max-w-[200px] truncate font-mono text-xs">{r.name}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.ttl || '—'}</TableCell>
                <TableCell className="max-w-[300px] truncate font-mono text-xs">
                  {r.aliasTarget ? r.aliasTarget.dnsName : r.values.join(', ')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InfrastructurePage() {
  const ec2 = useApi<EC2Data>('/api/infra/ec2');
  const rds = useApi<RDSData>('/api/infra/rds');
  const s3 = useApi<S3Data>('/api/infra/s3');
  const cloudfront = useApi<CloudFrontData>('/api/infra/cloudfront');
  const efs = useApi<EFSData>('/api/infra/efs');
  const route53 = useApi<Route53Data>('/api/infra/route53');

  const allApis = [ec2, rds, s3, cloudfront, efs, route53];
  const anyLoading = allApis.some((a) => a.loading);
  const errors = allApis.map((a) => a.error).filter((e): e is string => e !== null);
  const lastFetched =
    allApis
      .map((a) => a.lastFetched)
      .filter((d): d is Date => d !== null)
      .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;

  function refreshAll() {
    fetch('/api/cache/invalidate', { method: 'POST' })
      .catch(() => {})
      .finally(() => allApis.forEach((a) => a.refresh()));
  }

  const healthSystems = useMemo(
    () =>
      deriveHealth(
        ec2.data ?? null,
        rds.data ?? null,
        s3.data ?? null,
        cloudfront.data ?? null,
        efs.data ?? null
      ),
    [ec2.data, rds.data, s3.data, cloudfront.data, efs.data]
  );

  return (
    <div className="flex flex-col">
      <Header
        title="Infrastructure Health"
        lastFetched={lastFetched}
        onRefresh={refreshAll}
        loading={anyLoading}
      />

      {/* Health summary bar — answers "is everything okay?" at a glance */}
      <HealthSummaryBar systems={healthSystems} />

      <div className="p-6">
        {/* Errors */}
        {errors.length > 0 && <ErrorState message={errors.join('; ')} onRetry={refreshAll} />}

        {/* Loading */}
        {anyLoading && !ec2.data && <LoadingState rows={4} />}

        {/* Primary systems — more visual weight, generous spacing */}
        <div className="space-y-10">
          {ec2.data && <EC2Section data={ec2.data} />}
          {rds.data && <RDSSection data={rds.data} />}
        </div>

        {/* Divider between primary and secondary */}
        {(s3.data || cloudfront.data || efs.data) && <hr className="my-10 border-border" />}

        {/* Secondary systems — lighter treatment */}
        <SecondaryServices
          s3={s3.data ?? null}
          cloudfront={cloudfront.data ?? null}
          efs={efs.data ?? null}
        />

        {/* Route 53 — collapsible, de-emphasized */}
        {route53.data && (
          <>
            <hr className="my-8 border-border" />
            <Route53Section data={route53.data} />
          </>
        )}
      </div>
    </div>
  );
}
